import { getUserDetailsBaseUrl } from './chatIdentity';

// Shared vehicle-specification model for Add Vehicle (chat), Edit Vehicle (Garage) and
// Vehicle Details, so all three use the same field names, labels, VIN rules and VIN
// decode mapping (#380). Field keys match the vehicles API (update_user_car / lookup).

export const VEHICLE_SPEC_FIELDS = [
    { key: 'year', label: 'Year', required: true },
    { key: 'make', label: 'Make', required: true },
    { key: 'model', label: 'Model', required: true },
    { key: 'trim', label: 'Trim' },
    { key: 'mileage', label: 'Mileage' },
    { key: 'engine', label: 'Engine' },
    { key: 'transmission', label: 'Transmission' },
    { key: 'drivetrain', label: 'Drivetrain' },
    { key: 'fuel_type', label: 'Fuel Type' },
    { key: 'plate_number', label: 'License Plate' },
    { key: 'vin', label: 'VIN' },
];

export const NOT_PROVIDED = 'Not provided';

export const TRANSMISSION_OPTIONS = ['Automatic', 'Manual', 'CVT', 'DCT', 'Other'];
export const DRIVETRAIN_OPTIONS = ['FWD', 'RWD', 'AWD', '4WD', 'Other'];
export const FUEL_TYPE_OPTIONS = ['Gasoline', 'Diesel', 'Hybrid', 'Plug-in Hybrid', 'Electric', 'Flex Fuel', 'Other'];

// Decoded specs that a VIN lookup can fill in.
export const VIN_DECODED_FIELDS = ['year', 'make', 'model', 'trim', 'engine', 'transmission', 'drivetrain', 'fuel_type'];

const clean = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

// ── VIN ──────────────────────────────────────────────────────────────────────

// Input hygiene: upper-case, only VIN characters (no I, O, Q), at most 17.
export const sanitizeVinInput = (value) => String(value || '')
    .toUpperCase()
    .replace(/[^A-HJ-NPR-Z0-9]/g, '')
    .slice(0, 17);

const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;
const TRANSLITERATION = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
    J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
    S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

// The ISO 3779 check digit (position 9) is mandatory for vehicles built for North America
// (VINs starting 1–5: US, Canada, Mexico). Many other manufacturers don't compute it, so
// it is only enforced there.
export const isCheckDigitApplicable = (vin) => /^[1-5]/.test(vin || '');

export const isVinCheckDigitValid = (vin) => {
    if (!VIN_PATTERN.test(vin || '')) return false;
    const sum = [...vin].reduce((total, ch, i) => {
        const value = /\d/.test(ch) ? Number(ch) : TRANSLITERATION[ch];
        return total + value * WEIGHTS[i];
    }, 0);
    const remainder = sum % 11;
    const expected = remainder === 10 ? 'X' : String(remainder);
    return vin[8] === expected;
};

export const VIN_FORMAT_ERROR = 'Enter a valid 17-character VIN (letters and numbers, no I, O, or Q).';
export const VIN_CHECK_DIGIT_ERROR = "This VIN doesn't look right (the check digit doesn't match). Please check each character.";

// '' when the VIN is acceptable (or empty), otherwise a user-facing reason.
export const getVinError = (rawVin) => {
    const vin = clean(rawVin).toUpperCase();
    if (!vin) return '';
    if (!VIN_PATTERN.test(vin)) return VIN_FORMAT_ERROR;
    if (isCheckDigitApplicable(vin) && !isVinCheckDigitValid(vin)) return VIN_CHECK_DIGIT_ERROR;
    return '';
};

// ── Decoded value → form option mapping ─────────────────────────────────────

export const normalizeTransmission = (value) => {
    const v = clean(value).toLowerCase();
    if (!v) return '';
    if (v.includes('cvt') || v.includes('continuously variable')) return 'CVT';
    if (v.includes('dct') || v.includes('dual-clutch') || v.includes('dual clutch')) return 'DCT';
    if (v.includes('manual')) return 'Manual';
    if (v.includes('auto')) return 'Automatic';
    return 'Other';
};

export const normalizeDrivetrain = (value) => {
    const v = clean(value).toLowerCase();
    if (!v) return '';
    if (v.includes('awd') || v.includes('all-wheel') || v.includes('all wheel')) return 'AWD';
    if (v.includes('4wd') || v.includes('4x4') || v.includes('four-wheel') || v.includes('4-wheel')) return '4WD';
    if (v.includes('fwd') || v.includes('front')) return 'FWD';
    if (v.includes('rwd') || v.includes('rear')) return 'RWD';
    return 'Other';
};

export const normalizeFuelType = (value) => {
    const v = clean(value).toLowerCase();
    if (!v) return '';
    if (v.includes('plug')) return 'Plug-in Hybrid';
    if (v.includes('hybrid')) return 'Hybrid';
    if (v.includes('electric') && !v.includes('gas')) return 'Electric';
    if (v.includes('diesel')) return 'Diesel';
    if (v.includes('flex') || v.includes('ffv') || v.includes('e85')) return 'Flex Fuel';
    if (v.includes('gas') || v.includes('petrol')) return 'Gasoline';
    return 'Other';
};

// NHTSA decode payload → our field keys / option values (empty values dropped).
export const mapDecodedVehicle = (decoded = {}) => {
    const mapped = {
        year: clean(decoded.year),
        make: clean(decoded.make),
        model: clean(decoded.model),
        trim: clean(decoded.trim),
        engine: clean(decoded.engine),
        transmission: normalizeTransmission(decoded.transmission),
        drivetrain: normalizeDrivetrain(decoded.drivetrain),
        fuel_type: normalizeFuelType(decoded.fuel_type),
    };
    return Object.fromEntries(Object.entries(mapped).filter(([, v]) => v));
};

// Once a VIN decodes, it is the source of truth: every spec the VIN provides replaces the
// form value and is locked (read-only) until the VIN changes. Fields the VIN doesn't
// provide stay editable. `replaced` lists entered values the VIN changed, so the UI can
// say so instead of changing them silently.
export const applyVinSpecs = (form, decoded, keyMap = {}) => {
    const next = { ...form };
    const locked = [];
    const replaced = [];
    VIN_DECODED_FIELDS.forEach((key) => {
        const value = decoded[key];
        if (!value) return;
        const formKey = keyMap[key] || key;
        const current = clean(form[formKey]);
        const same = current.toLowerCase() === String(value).toLowerCase();
        if (current && !same) {
            const field = VEHICLE_SPEC_FIELDS.find((f) => f.key === key);
            replaced.push({ key, label: field ? field.label : key, previous: current, decoded: value });
        }
        // Same value in another case ("Toyota" vs "TOYOTA"): keep the entered spelling.
        next[formKey] = same ? current : value;
        locked.push(formKey);
    });
    return { next, locked, replaced };
};

export const replacedNote = (replaced) => (replaced.length
    ? `Updated from the VIN: ${replaced.map((r) => `${r.label} (was ${r.previous})`).join(', ')}.`
    : '');

export const VIN_LOCKED_NOTE = 'Details from the VIN are locked. Change the VIN to edit them.';

// Calls the existing decode endpoint. `unavailable` = the service could not be reached /
// was busy (manual entry should stay possible); otherwise an error means the VIN itself
// was rejected.
export async function lookupVin(rawVin) {
    const vin = clean(rawVin).toUpperCase();
    const vinError = getVinError(vin);
    if (!vin || vinError) return { ok: false, error: vinError || 'Enter a VIN first.' };
    try {
        const resp = await fetch(`${getUserDetailsBaseUrl()}/api/decode_vin/${encodeURIComponent(vin)}`);
        const data = await resp.json().catch(() => ({}));
        if (resp.ok && data?.status === 'success' && data?.vehicle) {
            return { ok: true, specs: mapDecodedVehicle(data.vehicle) };
        }
        const busy = !resp.ok && resp.status >= 500;
        const message = data?.error || "Couldn't decode this VIN.";
        return { ok: false, unavailable: busy || /busy|returned status|try again/i.test(message), error: message };
    } catch (_) {
        return { ok: false, unavailable: true, error: "Couldn't reach the VIN lookup service." };
    }
}

// Value shown in Vehicle Details.
export const displaySpec = (value) => {
    const text = clean(value);
    return text || NOT_PROVIDED;
};
